import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { comics, chapters, categories } from '@/mocks/manga';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find(c => c.id === Number(id));

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-20 text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
          <p className="text-foreground-500 mb-6">该漫画不存在或已被下架</p>
          <Link to="/" className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-lg transition-all text-sm whitespace-nowrap cursor-pointer">
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const comicChapters = chapters.filter(ch => ch.comicId === comic.id);
  const relatedComics = comics.filter(c => c.category === comic.category && c.id !== comic.id).slice(0, 6);
  const categoryObj = categories.find(c => c.slug === comic.category);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 古古漫画网`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.updateDate,
    publisher: {
      '@type': 'Organization',
      name: '古古漫画网',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.gugumanga.com/article/${comic.id}`,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb items={[
              { label: '首页', href: '/' },
              ...(categoryObj ? [{ label: categoryObj.name, href: `/category/${categoryObj.slug}` }] : []),
              { label: comic.title },
            ]} />

            <div className="flex flex-col lg:flex-row gap-8 mt-4">
              <div className="flex-shrink-0">
                <div className="w-full sm:w-64 lg:w-72 aspect-[3/4] rounded-xl overflow-hidden">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - 古古漫画网`}
                    title={`${comic.title} - ${comic.tags.join(' ')}`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                  {comic.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 mt-3">
                  <span className="text-sm text-foreground-600">
                    <i className="ri-user-line mr-1"></i>{comic.author}
                  </span>
                  <span className="text-sm text-foreground-600">
                    <i className="ri-star-fill text-amber-400 mr-1"></i>{comic.rating} 分
                  </span>
                  <span className="text-sm text-foreground-600">
                    <i className="ri-eye-line mr-1"></i>{(comic.views / 10000).toFixed(0)} 万阅读
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    comic.status === '连载' ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {comic.status}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-1.5 mt-4">
                  {comic.tags.map(tag => (
                    <Link
                      key={tag}
                      to={`/category/${comic.category}`}
                      className="px-3 py-1 text-xs rounded-full bg-primary-50 text-primary-600 hover:bg-primary-100 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {tag}
                    </Link>
                  ))}
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mt-5 bg-background-100 rounded-xl p-4 border border-background-200/70">
                  <strong className="text-foreground-800">简介：</strong>{comic.description}
                </p>

                <div className="flex items-center gap-3 mt-6">
                  <button className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-lg transition-all text-sm whitespace-nowrap cursor-pointer">
                    <i className="ri-play-fill mr-1.5"></i>开始阅读
                  </button>
                  <button className="px-6 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 font-medium rounded-lg border border-background-200/70 transition-all text-sm whitespace-nowrap cursor-pointer">
                    <i className="ri-bookmark-line mr-1.5"></i>收藏
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-10">
              <h2 className="text-lg md:text-xl font-bold text-foreground-900 font-heading mb-5">
                章节目录 <span className="text-sm font-normal text-foreground-500 ml-2">共 {comic.chapters} 话</span>
              </h2>
              <div className="bg-background-100 rounded-xl border border-background-200/70 overflow-hidden">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-background-200/50">
                  {comicChapters.map(ch => (
                    <a
                      key={ch.id}
                      href="#"
                      className="flex items-center justify-between px-4 py-3 bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer"
                    >
                      <span className="text-sm text-foreground-700 font-medium line-clamp-1">{ch.title}</span>
                      <span className="text-xs text-foreground-400 flex-shrink-0 ml-2">{ch.date}</span>
                    </a>
                  ))}
                </div>
                {comic.chapters > comicChapters.length && (
                  <div className="text-center py-4 border-t border-background-200/50">
                    <button className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer">
                      展开全部章节 <i className="ri-arrow-down-s-line ml-1"></i>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {relatedComics.length > 0 && (
              <div className="mt-10">
                <h2 className="text-lg md:text-xl font-bold text-foreground-900 font-heading mb-5">相关推荐</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
                  {relatedComics.map(rc => (
                    <Link
                      key={rc.id}
                      to={`/article/${rc.id}`}
                      className="group cursor-pointer"
                      data-product-shop
                    >
                      <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-2.5">
                        <img
                          src={rc.cover}
                          alt={`${rc.title} - 古古漫画网`}
                          title={`${rc.title} - ${rc.tags.join(' ')}`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                        />
                      </div>
                      <h3 className="text-xs font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                        {rc.title}
                      </h3>
                      <p className="text-[11px] text-foreground-500 mt-0.5">{rc.author}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}